import { useEffect, useMemo, useState } from 'react'
import {
  Ban,
  CalendarClock,
  MessageCircle,
  Phone,
  User,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { canCancelAppointment, canMarkAppointmentNoShow } from '@/lib/calendar/cancellationReasons'
import { canRescheduleAppointment } from '@/lib/calendar/appointmentLayout'
import {
  canCheckInAppointment,
  formatArrivalTime,
} from '@/lib/calendar/checkIn'
import { buildBookableTimeOptions } from '@/lib/calendar/bookAppointment'
import {
  formatMinutesLabel,
  isoToMinutes,
} from '@/lib/calendar/dayCalendar'
import {
  buildBookingWhatsAppMessage,
  openWhatsAppBooking,
} from '@/lib/calendar/appointmentWhatsApp'
import { formatE164Display } from '@/lib/phone'
import { formatAppointmentStatus } from '@/lib/appointmentStatusLabels'

/**
 * @param {import('@/types/api').Appointment} appointment
 */
function serviceLines(appointment) {
  const lines = appointment.services ?? []
  if (lines.length === 0) {
    return ['Appointment']
  }

  return lines.map((line) => {
    const name = line.service?.name ?? 'Service'
    const stylist = line.stylist?.name
    return stylist ? `${name} · ${stylist}` : name
  })
}

/**
 * @param {{
 *   open: boolean
 *   appointment: import('@/types/api').Appointment | null
 *   stylists: import('@/types/api').Stylist[]
 *   openTime: string
 *   closeTime: string
 *   slotMinutes: number
 *   onClose: () => void
 *   onReschedule?: (appointmentId: number, payload: { starts_at: string, stylist_id?: number | null }) => Promise<void>
 *   onApproveOnline?: (appointmentId: number) => Promise<void>
 *   onOpenOutcome?: (appointment: import('@/types/api').Appointment) => void
 *   onCheckIn?: (appointment: import('@/types/api').Appointment) => Promise<void>
 *   onStartService?: (appointment: import('@/types/api').Appointment) => Promise<void>
 *   rescheduling?: boolean
 * }} props
 */
export function AppointmentDetailModal({
  open,
  appointment,
  stylists,
  openTime,
  closeTime,
  slotMinutes,
  onClose,
  onReschedule,
  onApproveOnline,
  onOpenOutcome,
  onCheckIn,
  onStartService,
  rescheduling = false,
}) {
  const { user } = useAuth()
  const { activeBranch } = useBranch()
  const [showReschedule, setShowReschedule] = useState(false)
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [rescheduleTime, setRescheduleTime] = useState('')
  const [rescheduleStylistId, setRescheduleStylistId] = useState('')
  const [error, setError] = useState('')
  const [approving, setApproving] = useState(false)
  const [statusBusy, setStatusBusy] = useState(false)

  const activeStylists = useMemo(
    () => stylists.filter((stylist) => stylist.is_active),
    [stylists],
  )

  const durationMinutes = appointment?.services?.[0]?.duration_minutes
    ?? appointment?.duration_minutes
    ?? 30

  const timeOptions = useMemo(
    () => buildBookableTimeOptions(openTime, closeTime, slotMinutes, durationMinutes),
    [closeTime, durationMinutes, openTime, slotMinutes],
  )

  useEffect(() => {
    if (!open || !appointment) {
      return
    }

    setShowReschedule(false)
    setError('')
    setRescheduleDate(appointment.starts_at.slice(0, 10))
    setRescheduleTime(formatMinutesLabel(isoToMinutes(appointment.starts_at)))
    setRescheduleStylistId(appointment.stylist_id ? String(appointment.stylist_id) : '')
  }, [appointment, open])

  if (!open || !appointment) {
    return null
  }

  const guestName = appointment.customer?.name ?? 'Guest'
  const guestPhone = appointment.customer?.phone ?? null
  const canReschedule = canRescheduleAppointment(appointment.status)
  const canOutcome = canCancelAppointment(appointment) || canMarkAppointmentNoShow(appointment)
  const salonName = user?.company?.name ?? activeBranch?.name ?? 'our salon'

  const whatsappMessage = buildBookingWhatsAppMessage({
    appointments: [appointment],
    customer: appointment.customer ?? null,
    salonName,
    branchName: activeBranch?.name ?? null,
  })

  async function handleRescheduleSubmit(event) {
    event.preventDefault()

    if (!onReschedule || !rescheduleDate || !rescheduleTime) {
      return
    }

    setError('')

    try {
      await onReschedule(appointment.id, {
        starts_at: `${rescheduleDate}T${rescheduleTime}:00`,
        stylist_id: rescheduleStylistId ? Number(rescheduleStylistId) : null,
      })
      setShowReschedule(false)
      onClose()
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to reschedule appointment.')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-foreground/35 backdrop-blur-[1px]"
        aria-label="Close appointment details"
        onClick={onClose}
      />
      <div
        className="relative z-10 flex h-full w-full max-w-[28rem] flex-col border-l border-border/70 bg-card shadow-luxury-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-detail-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border/60 px-5 py-4">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Appointment
            </p>
            <h2 id="appointment-detail-title" className="mt-1 truncate text-lg font-semibold text-foreground">
              {guestName}
            </h2>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {formatArrivalTime(appointment.starts_at)}
            </p>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-foreground">
              {formatAppointmentStatus(appointment.status)}
            </span>
            {appointment.source === 'online' ? (
              <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-sky-800">
                Online booking
              </span>
            ) : null}
            {appointment.online_pending_approval ? (
              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                Awaiting approval
              </span>
            ) : null}
            {appointment.source === 'online' && appointment.online_approval_label ? (
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-900">
                Approved: {appointment.online_approval_label}
              </span>
            ) : null}
            {appointment.is_bridal_event ? (
              <span className="rounded-full bg-gold/20 px-2.5 py-1 text-[10px] font-bold text-[hsl(31_45%_35%)]">
                Bridal / event
              </span>
            ) : null}
          </div>

          <dl className="grid gap-3 text-sm">
            <div className="flex gap-3">
              <User className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Guest</dt>
                <dd className="font-medium text-foreground">{guestName}</dd>
                {guestPhone ? (
                  <dd className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                    <Phone className="h-3 w-3" aria-hidden />
                    {formatE164Display(guestPhone)}
                  </dd>
                ) : null}
              </div>
            </div>

            <div className="flex gap-3">
              <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">When</dt>
                <dd className="font-medium text-foreground">
                  {new Date(appointment.starts_at).toLocaleString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </dd>
                <dd className="mt-0.5 text-xs text-muted-foreground">
                  {appointment.duration_minutes} minutes
                </dd>
              </div>
            </div>

            <div>
              <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Services</dt>
              <dd className="mt-1 space-y-1">
                {serviceLines(appointment).map((line) => (
                  <p key={line} className="text-sm text-foreground">{line}</p>
                ))}
              </dd>
            </div>

            {appointment.stylist?.name ? (
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Stylist</dt>
                <dd className="text-sm font-medium text-foreground">{appointment.stylist.name}</dd>
              </div>
            ) : null}

            {appointment.chair?.name ? (
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Chair</dt>
                <dd className="text-sm font-medium text-foreground">{appointment.chair.name}</dd>
              </div>
            ) : null}

            {appointment.source ? (
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Source</dt>
                <dd className="text-sm capitalize text-foreground">{appointment.source.replace(/_/g, ' ')}</dd>
              </div>
            ) : null}

            {appointment.notes?.trim() ? (
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Notes</dt>
                <dd className="text-sm text-foreground">{appointment.notes.trim()}</dd>
              </div>
            ) : null}
          </dl>

          {showReschedule && canReschedule ? (
            <form
              onSubmit={handleRescheduleSubmit}
              className="space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-4"
            >
              <p className="text-sm font-semibold text-foreground">Reschedule booking</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Date
                  </span>
                  <input
                    type="date"
                    required
                    value={rescheduleDate}
                    onChange={(event) => setRescheduleDate(event.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Time
                  </span>
                  <select
                    required
                    value={rescheduleTime}
                    onChange={(event) => setRescheduleTime(event.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  >
                    {timeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Stylist
                </span>
                <select
                  value={rescheduleStylistId}
                  onChange={(event) => setRescheduleStylistId(event.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Unassigned</option>
                  {activeStylists.map((stylist) => (
                    <option key={stylist.id} value={stylist.id}>
                      {stylist.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex gap-2">
                <Button type="submit" disabled={rescheduling}>
                  {rescheduling ? 'Saving…' : 'Save new time'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowReschedule(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : null}

          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 border-t border-border/60 px-5 py-4">
          {appointment.online_pending_approval && onApproveOnline ? (
            <Button
              type="button"
              disabled={approving}
              onClick={async () => {
                setApproving(true)
                setError('')
                try {
                  await onApproveOnline(appointment.id)
                  onClose()
                } catch (err) {
                  setError(err?.response?.data?.message ?? 'Unable to approve booking.')
                } finally {
                  setApproving(false)
                }
              }}
            >
              {approving ? 'Approving…' : 'Approve online booking'}
            </Button>
          ) : null}

          {canCheckInAppointment(appointment) && onCheckIn ? (
            <Button
              type="button"
              disabled={statusBusy}
              onClick={async () => {
                setStatusBusy(true)
                setError('')
                try {
                  await onCheckIn(appointment)
                } catch (err) {
                  setError(err?.response?.data?.message ?? 'Unable to check in guest.')
                } finally {
                  setStatusBusy(false)
                }
              }}
            >
              {statusBusy ? 'Updating…' : 'Check in'}
            </Button>
          ) : null}

          {appointment.status === 'checked_in' && onStartService ? (
            <Button
              type="button"
              disabled={statusBusy}
              onClick={async () => {
                setStatusBusy(true)
                setError('')
                try {
                  await onStartService(appointment)
                } catch (err) {
                  setError(err?.response?.data?.message ?? 'Unable to start service.')
                } finally {
                  setStatusBusy(false)
                }
              }}
            >
              {statusBusy ? 'Updating…' : 'Start service'}
            </Button>
          ) : null}

          {appointment.status === 'in_chair' ? (
            <p className="rounded-lg bg-primary/10 px-3 py-2 text-center text-sm font-medium text-primary">
              In service
            </p>
          ) : null}

          {appointment.status === 'completed' ? (
            <p className="rounded-lg bg-muted px-3 py-2 text-center text-sm font-medium text-muted-foreground">
              Completed
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-1">
            {canReschedule && onReschedule ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReschedule((current) => !current)}
              >
                Reschedule
              </Button>
            ) : null}
            {guestPhone ? (
              <Button
                type="button"
                variant="outline"
                className="border-[#25D366]/40 text-[#1a9f4f] hover:bg-[#25D366]/10"
                onClick={() => openWhatsAppBooking({ phone: guestPhone, message: whatsappMessage })}
              >
                <MessageCircle className="mr-1.5 h-4 w-4" aria-hidden />
                WhatsApp
              </Button>
            ) : null}
            {canOutcome && onOpenOutcome ? (
              <Button
                type="button"
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
                onClick={() => {
                  onOpenOutcome(appointment)
                  onClose()
                }}
              >
                <Ban className="mr-1.5 h-4 w-4" aria-hidden />
                Cancel / no-show
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
